import { Globe, Menu } from "lucide-react";
import { useMatches } from "react-router";
import { useViewerTimezone } from "@/shared/hooks/useViewerTimezone";
import { Button } from "@/shared/ui/button";
import { Icon } from "@/shared/ui/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { InternalBadge } from "./Brand";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";

interface RouteHandle {
  title: string;
}

function isRouteHandle(h: unknown): h is RouteHandle {
  return typeof h === "object" && h !== null && typeof (h as RouteHandle).title === "string";
}

export function usePageTitle(): string {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.handle;
    if (isRouteHandle(handle)) return handle.title;
  }
  return "NexBorg Access Internal";
}

interface TopBarProps {
  onOpenMenu: () => void;
}

/** 52 px: title left; the viewer's zone, theme and the operator right (design-system §4). */
export function TopBar({ onOpenMenu }: TopBarProps) {
  const title = usePageTitle();
  const tz = useViewerTimezone();
  return (
    <header className="flex h-13 shrink-0 items-center gap-3 border-b border-border bg-surface px-4 md:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenMenu}
        aria-label="Open navigation"
      >
        <Icon icon={Menu} size={20} />
      </Button>
      <h2 className="min-w-0 flex-1 truncate text-body font-semibold text-fg">{title}</h2>
      <InternalBadge className="lg:hidden" />
      <div className="flex items-center gap-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="hidden items-center gap-1.5 rounded-sm px-2 text-caption text-fg-muted md:inline-flex">
              <Icon icon={Globe} size={16} />
              <span className="font-mono">{tz}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Times are shown in your browser&apos;s zone, not the organisation&apos;s.
          </TooltipContent>
        </Tooltip>
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
